import {
  JobStatus,
  PartImageEvidenceTier,
  PartImageIdentification,
  PartImageIdentificationOutcome,
} from '@/api/generatedApi';
import { ApiErrorAlert } from '@/components/Errors/ApiErrorAlert';
import { Thumbnail } from '@/components/Thumbnail';
import { useCreateReportFromPartImageCandidate } from '@/hooks/api/part-finder-image-search/useCreateReportFromPartImageCandidate';
import { useGetPartImageIdentification } from '@/hooks/api/part-finder-image-search/useGetPartImageIdentification';
import { useToast } from '@/hooks/useToast';
import { useTenants } from '@/services/tenants/useTenants';
import { getApiError } from '@/types/apiError';
import {
  Alert,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { keyframes } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import { IdentificationProgress, identificationStep } from './IdentificationProgress';
import { MultipleItemsResult } from './MultipleItemsResult';
import { NarrowByMeasurements } from './NarrowByMeasurements';
import { PartImageIdentificationResult } from './PartImageIdentificationResult';

const SUBMITTED_THUMB_SIZE = 48;
const PANES_SCROLL_ON_THEIR_OWN = { display: { md: 'flex' }, flexDirection: 'column', overflowY: { md: 'hidden' } };
const DIALOG_PAPER = { borderRadius: '12px', height: { md: 'min(880px, calc(100% - 64px))' } };

const sweep = keyframes`
  from { transform: translateY(-100%); }
  to { transform: translateY(100%); }
`;

const ScanSweep = styled(Box)(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
  background: `linear-gradient(180deg, transparent 0%, ${alpha(theme.palette.info.light, 0.5)} 50%, transparent 100%)`,
  animation: `${sweep} 1.6s ease-in-out infinite`,
  '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 0 },
}));

const isFinished = (identification?: PartImageIdentification): identification is PartImageIdentification =>
  identification?.status === JobStatus.COMPLETED || identification?.status === JobStatus.FAILED;

const NOT_NARROWABLE = new Set<PartImageIdentificationOutcome | undefined>([
  PartImageIdentificationOutcome.ReportCreated,
  PartImageIdentificationOutcome.NoPart,
  PartImageIdentificationOutcome.MultipleItems,
]);

export const canNarrow = (identification: PartImageIdentification): boolean =>
  !NOT_NARROWABLE.has(identification.outcome) &&
  identification.candidates.every(candidate => candidate.evidenceTier === PartImageEvidenceTier.Inferred);

const SubmittedImages = ({ images, scanning }: { images: File[]; scanning: boolean }) => (
  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }} data-testid='part-image-submitted'>
    {images.map((image, index) => (
      <Box
        key={`${image.name}-${index}`}
        sx={{
          position: 'relative',
          width: SUBMITTED_THUMB_SIZE,
          height: SUBMITTED_THUMB_SIZE,
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        <Thumbnail file={image} size={SUBMITTED_THUMB_SIZE} noLink />
        {scanning && <ScanSweep data-testid='part-image-scanning' />}
      </Box>
    ))}
  </Box>
);

export interface ImagePartFinderModalProperties {
  images: File[];
  identificationId?: string;
  isSubmitting: boolean;
  submitError?: Error;
  onTryOtherPhotos: () => void;
  onClose: () => void;
}

export const ImagePartFinderModal = ({
  images,
  identificationId,
  isSubmitting,
  submitError,
  onTryOtherPhotos,
  onClose,
}: ImagePartFinderModalProperties) => {
  const createReport = useCreateReportFromPartImageCandidate();
  const { data: identification, error: pollError } = useGetPartImageIdentification(identificationId);
  const { showToast } = useToast();
  const { getPath } = useTenants();
  const navigate = useNavigate();

  const openReport = (reportId: string) => {
    navigate(getPath(`part-finder-reports/${reportId}`));
    onClose();
  };

  const openBatch = (batchId: string) => {
    showToast({ message: 'Part finder batch created successfully', type: 'success' });
    navigate(getPath(`part-finder/batches/${batchId}`));
    onClose();
  };

  const handleCreateReport = (niin: string) => {
    if (!identificationId) return;
    createReport.mutate(
      { identificationId, niin },
      {
        onSuccess: report => {
          showToast({ message: 'Part finder report created successfully', type: 'success' });
          openReport(report.id);
        },
      }
    );
  };

  const isIdentifying = isSubmitting || (identificationId !== undefined && !pollError && !isFinished(identification));
  const isComplete = identification?.status === JobStatus.COMPLETED;
  const narrowable = isComplete && canNarrow(identification);
  const multipleItems = isComplete && identification.outcome === PartImageIdentificationOutcome.MultipleItems;
  const apiError = getApiError(submitError ?? pollError ?? createReport.error ?? undefined, 'Image search failed');

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth='lg'
      fullWidth
      slotProps={{ paper: { sx: DIALOG_PAPER } }}
      data-testid='image-part-finder-modal'
    >
      <DialogTitle sx={{ pb: 1 }}>Find from images</DialogTitle>
      <Collapse in={isIdentifying}>
        <LinearProgress color='info' sx={{ mx: 3, mb: 1, height: 4, borderRadius: 2, bgcolor: 'action.hover' }} />
      </Collapse>
      <DialogContent sx={{ pt: 1, ...PANES_SCROLL_ON_THEIR_OWN }}>
        <Stack gap='16px' sx={{ flex: { md: 1 }, minHeight: 0 }}>
          <Stack direction='row' columnGap='24px' rowGap='12px' alignItems='center' flexWrap='wrap'>
            <SubmittedImages images={images} scanning={isIdentifying} />
            {isIdentifying && <IdentificationProgress step={identificationStep(identificationId, identification)} />}
          </Stack>
          <ApiErrorAlert error={apiError} />
          {identification?.status === JobStatus.FAILED && (
            <Alert severity='error'>{identification.failureReason ?? 'Identification failed. Try again.'}</Alert>
          )}
          {multipleItems && (
            <MultipleItemsResult
              key={identification.items.map(item => item.photoNumbers.join('-')).join('|')}
              identification={identification}
              images={images}
              onBatchCreated={openBatch}
            />
          )}
          {(isIdentifying || isComplete) && !multipleItems && (
            <PartImageIdentificationResult
              identification={identification}
              isCreating={createReport.isPending}
              onCreateReport={handleCreateReport}
              onOpenReport={openReport}
              narrowing={narrowable && <NarrowByMeasurements identificationId={identification.id} />}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', borderRadius: '12px' }}>
          Cancel
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={onTryOtherPhotos}
          disabled={isIdentifying}
          sx={{ textTransform: 'none', borderRadius: '12px' }}
          data-testid='part-image-start-over'
        >
          Try other photos
        </Button>
      </DialogActions>
    </Dialog>
  );
};
